import React, { useState, useEffect } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../common/Table";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../common/FormRenderer";                                                                                                  
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import {
  useGetVerticals,
  useGetDepartments,
  useGetDepartmentUserMappings,
} from "../../../backend/masters";
import {
  useBulkAssignCompliances,
} from "../../../backend/compliance";
import { USER_PRIVILEGES } from "../UserManagement/Roles/RoleConfiguration";

interface Props {
  companyId?: string;
  pageFilters?: any;
  total?: number;
  onCancel: () => void;
}

export default function BulkAssignModal({ companyId, pageFilters, total, onCancel }: Props) {
  const [vertical, setVertical] = useState<any>(null);
  const [department, setDepartment] = useState<any>(null);
  const [owners, setOwners] = useState<any[]>([]);
  const [managers, setManagers] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});                                                                                                                                                                
  const [data, setData] = useState<any>({ hideButtons: true });

  const { verticals } = useGetVerticals(
    { ...DEFAULT_OPTIONS_PAYLOAD, filters: { company_id_eq: companyId } },
    Boolean(companyId),
  );
  const { departments } = useGetDepartments(
    {
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { vertical_id_eq: vertical ? vertical.value : undefined },
    },
    Boolean(vertical),
  );
  const { departmentUsers, isFetching: fetchingUsers } =
    useGetDepartmentUserMappings(
      {
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
          department_id_eq: department ? department.value : undefined,
          is_active_eq: true,
        },
      },
      Boolean(department),
    );

  const { bulkAssignCompliances, assigning } = useBulkAssignCompliances(
    () => {
      toast.success(
        "Bulk assignment is in progress. You will be notified once complete.",
      );
      onCancel();
    },
    () => toast.error("Failed to trigger bulk assignment. Please try again."),
  );

  const schema = {                                                                                                                                                                                          
      fields: [                                         
          {                                                                                                                                                                                                 
              component: componentTypes.SELECT,
              name: 'vertical',                                                                                                                                                                             
              label: 'Vertical',                        
              options: verticals,
              onChange: handleVerticalChange,                                                                                                                                                               
              validate: [{ type: validatorTypes.REQUIRED }]
          },                                                                                                                                                                                                
          {                                             
              component: componentTypes.SELECT,                                                                                                                                                             
              name: 'department',                       
              label: 'Department',
              options: departments,
              onChange: handleDepartmentChange,
              validate: [{ type: validatorTypes.REQUIRED }]
          },                                                                                                                                                                                                
          {
              component: componentTypes.SELECT,                                                                                                                                                             
              name: 'owner',                            
              label: 'Owner',
              options: owners,
              validate: [{ type: validatorTypes.REQUIRED }]
          },
          {
              component: componentTypes.SELECT,
              name: 'manager',                                                                                                                                                                              
              label: 'Manager',
              options: managers,                                                                                                                                                                            
              validate: [{ type: validatorTypes.REQUIRED }]
          }
      ]
  };

  useEffect(() => {
    if (fetchingUsers) {
      setOwners([]);
      setManagers([]);
      return;
    }
    if (!fetchingUsers && departmentUsers) {
      const _owners = departmentUsers
        .filter(
          ({ user }: any) =>
            user &&
            (user.roles || []).find((role: any) =>
              role.pages.includes(USER_PRIVILEGES.OWNER_DASHBOARD),
            ),
        )
        .map(({ user }: any) => user);
      const _managers = departmentUsers
        .filter(
          ({ user }: any) =>
            user &&
            (user.roles || []).find((role: any) =>
              role.pages.includes(USER_PRIVILEGES.MANAGER_DASHBOARD),
            ),
        )
        .map(({ user }: any) => user);
      setOwners(_owners);
      setManagers(_managers);
    }
  }, [fetchingUsers]);

  function handleVerticalChange(e: any) {                                                                                                                                                                   
      setVertical(e);                                   
      setDepartment(null);
  }                                                                                                                                                                                                         
   
  function handleDepartmentChange(e: any) {                                                                                                                                                                 
      setDepartment(e);                                 
  }

  function debugForm(_form: any) {
      setForm(_form);
      setData(_form.values);
  }

  function handleSubmit() {
    if (!form.valid || assigning) return;
    const { vertical, department, owner, manager } = data;
    bulkAssignCompliances({
      page_filters: pageFilters || {},
      company_id: companyId,
      vertical_id: vertical.value,
      department_id: department.value,
      compliance_owner_id: owner.value,
      compliance_manager_id: manager.value,
    });
  }

  return (
    <>
      <Modal
        show={true}
        backdrop="static"
        dialogClassName="drawer"
        animation={false}
      >
        <Modal.Header closeButton={true} onHide={onCancel}>
          <Modal.Title className="bg">Bulk Assign Owner / Manager</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
              <p className="text-muted mb-0">
                The owner/manager will be assigned to{" "}
                <strong>{(total || 0).toLocaleString()}</strong> compliance
                records.
              </p>
          </div>

          <FormRenderer                                     
              FormTemplate={FormTemplate}
              initialValues={data}
              componentMapper={ComponentMapper}
              schema={schema}                                                                                                                                                                                   
              debug={debugForm}
          />                                                                                                                                                                                                    
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            className="px-4"
            onClick={onCancel}
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="px-4"
            onClick={handleSubmit}
            disabled={!form.valid || assigning}
          >
            {assigning ? (
              <>
                <Spinner size="sm" className="me-2" />
                Assigning...
              </>
            ) : (
              "Bulk Assign"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
